import { forwardRef, useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import ClearInputAdornment from './ClearInputAdornment';
import { IMaskInput } from 'react-imask';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { CustomFieldProps } from './types';
import { formatPhoneNumberDashes } from '@components/PhoneLink';
import useDebouncer from '@hooks/useDebounce';

import styles from './Form.module.css';

const classNamesCtx = classNames.bind(styles);

interface PhoneMaskInputProps {
  /** String of the input label */
  labelText: string;
  /** Flag indicating the input is dirty and should apply styles */
  isDirty?: boolean;
  /** Flag indicating the input is in an error state */
  error?: boolean;
  /** When the input is in an error state, text to display in the tooltip */
  errorText?: string;
  /** Function to execute when the clear button is clicked */
  onClear?: () => void;
  /** Props applied to the underlying IMaskInput */
  inputProps: PhoneMaskBaseProps;
}

interface PhoneMaskBaseProps {
  /** Required input name. Based HTML Input Element is optional. */
  name: string;
  /** Input value */
  value: string;
  /** Input On Change event */
  onChange: (event: { target: { name: string; value: string } }) => void;
  /** Input On Blur event */
  onBlur: (event: { target: { name: string; value: string } }) => void;
  /** CSS Class Names */
  className?: string;
  /** Flag indicating the input is in a disabled state */
  disabled?: boolean;
}

/**
 * Wrapper around the IMaskInput to persist MUI Input styles while utilizing Input Masks for Phone Numbers
 */
export default function PhoneMaskInput(props: PhoneMaskInputProps) {
  const { inputProps, ...others } = props;

  const formControlClass = classNamesCtx({
    input: true,
    dirty: others.isDirty,
    error: others.error,
    disabled: inputProps.disabled,
    [inputProps.className ?? '']: Boolean(inputProps.className)
  });

  const outlinedInputClass = classNamesCtx({
    input: true,
    root: true
  });

  return (
    <FormControl
      variant='outlined'
      className={formControlClass}>
      <InputLabel htmlFor={inputProps.name}>
        <FormLabel
          text={others.labelText}
          error={others.error && !inputProps.disabled}
          errorText={others.errorText}
        />
      </InputLabel>
      <OutlinedInput
        autoComplete='one-time-code'
        {...inputProps}
        className={outlinedInputClass}
        id={inputProps.name}
        inputComponent={TextMaskCustom as never}
        label={`${others.labelText} ${props.error ? 'er' : ''}`} // This is used for the break in the fieldset, since we add an error icon when it is an error add extra space
        error={others.error}
        endAdornment={
          <ClearInputAdornment
            onClear={others.onClear}
            inputValue={inputProps.value}
            inputIsDisabled={inputProps.disabled}
          />
        }
      />
    </FormControl>
  );
}

/** React iMask Input Component applying the Phone Number Mask as (###) ###-#### */
const TextMaskCustom = forwardRef<HTMLInputElement, PhoneMaskBaseProps>(function RenderTextMaskCustom(props, ref) {
  return (
    <IMaskInput
      name={props.name}
      type='tel'
      className={props.className}
      value={props.value}
      disabled={props.disabled}
      mask='(###) ###-####'
      unmask
      definitions={{
        '#': /[0-9]/
      }}
      placeholder='(###) ###-####'
      inputRef={ref}
      onAccept={(value: string) => {
        props.onChange({ target: { name: props.name, value } });
      }}
      onBlur={props.onBlur}
      overwrite
    />
  );
});

function asPhoneInput() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<string | null>(field.name);

    const { dirty, validateForm } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        /** Get just the numeric values of the string */
        return value?.replaceAll(/\D/g, '') !== initialValue?.replaceAll(/\D/g, '');
      }
      return false;
    }, [value, initialValue, dirty]);

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState<string>(value ?? '');

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      setMyValue((state) => {
        if ((value ?? '') !== state) {
          return value ?? '';
        }
        return state;
      });
      void validateForm();
    }, [value, validateForm]);

    /** When the local state has changed, compare to the form value. After a short delay, update the form if the value differs. */
    useDebouncer(
      () => {
        if (myValue !== (value ?? '')) {
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, myValue, true);
          void validateForm();
        }
      },
      [myValue],
      300
    );

    return (
      <PhoneMaskInput
        inputProps={{
          name: props.name,
          value: myValue,
          onChange: (event) => {
            /** Phones are saved in Trinium as ###-###-#### so when we have the entire phone number string
             *  add the dashes to the value used for updating the formik form. When we have partial values,
             *  simply use whatever the current value is */
            const formattedPhone = formatPhoneNumberDashes(event.target.value);

            setMyValue(formattedPhone ? formattedPhone : event.target.value);
          },
          onBlur: () => {
            void form.setFieldTouched(field.name, true, true);
          },
          disabled: form.isSubmitting || props.disabled,
          className: props.className
        }}
        isDirty={isDirty}
        labelText={props.label ?? ''}
        error={Boolean(error)}
        errorText={error}
        onClear={() => {
          setMyValue('');
        }}
      />
    );
  };
}

export const FormikPhoneInput = asPhoneInput();
